import { inject, injectable } from 'inversify'
import type { Gridable } from '@/services/types'
import type { PaginationParams } from '@/services/tables/pagination/types'
import type { EndpointParams } from '@/services/endpoints'
import type { PaginationUrlType } from '@/services/tables/types'
import type User from '@/data/models/domain/User'
import type UserRepository from '@/data/repositories/domain/users/userRepository'
import { UserFilterName, type UserStatus } from '@/services/domain/user/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type TeamWorkflowService from '@/services/domain/accounts/teamWorkflowService'
import type UserService from '@/services/domain/user/userService'

@injectable()
export default class UsersGridable implements Gridable<User> {
  constructor(
    @inject(SERVICE_TYPES.UserService)
    protected readonly userService: UserService,
    @inject(SERVICE_TYPES.UserRepository)
    protected readonly userRepository: UserRepository,
    @inject(SERVICE_TYPES.TeamWorkflowService)
    protected readonly teamWorkflowService: TeamWorkflowService,
  ) {}

  public getFetchEndpointParams(): EndpointParams {
    return []
  }

  public async gridRequest(
    queryParameterBag: PaginationUrlType,
    paginationParamsBag?: PaginationParams,
    searchQuery?: string,
    searchFields?: string[],
  ) {
    const statuses = this.extractStatusesFromFilter(queryParameterBag)

    const queryParameterBagExtended = {
      ...queryParameterBag,
      other: {
        statuses,
      },
    }
    if (this.teamWorkflowService.isUsersWorkflow()) {
      ;(queryParameterBagExtended.other as { includeParent?: boolean }).includeParent = false
    } else {
      ;(queryParameterBagExtended.other as { includeSelf?: boolean }).includeSelf = false
    }

    return this.userRepository.gridRequest(queryParameterBagExtended, paginationParamsBag, searchQuery, searchFields)
  }

  protected extractStatusesFromFilter(queryParameterBag: PaginationUrlType): UserStatus[] {
    const statusFilter = queryParameterBag.filter?.find((filter) => filter[UserFilterName.STATUS] !== undefined)
    if (statusFilter) {
      const values: Array<string> = (statusFilter[UserFilterName.STATUS].in as Array<string>) ?? []
      const flatValues = values.join(',')
      return flatValues.length > 0
        ? (flatValues.split(',') as UserStatus[])
        : this.userService.getStatusesForAllSubAccount()
    }
    return this.userService.getStatusesForAllSubAccount()
  }
}
